import React, { useState, useEffect, useContext } from "react";
import { Form, Input, Button, Modal, Select, Spin, message } from "antd";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { calculateLoanPayments } from "../utils/loanCalculations";
import { Alert } from "antd";

const ApplyForNewLoanModal = ({ user, visible, onCancel }) => {
  const [form] = Form.useForm();
  const [loanTypes, setLoanTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const [maxAmount, setMaxAmount] = useState(0);
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const companyId =
    (typeof company === "object" ? company?.id : company) || null;

  useEffect(() => {
    if (visible && companyId) {
      fetchLoanTypes();
    }
  }, [visible, companyId]);

  const fetchLoanTypes = async () => {
    if (!companyId) {
      setLoanTypes([]);
      message.error("Unable to determine your company. Please sign in again.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loan_products?fields=*.*&filter[company]=${companyId}&filter[is_active][_eq]=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const types = response.data.data.map((item) => ({
        label: item.loan_name,
        value: item.id,
        ...item,
      }));
      setLoanTypes(types);
      if (types.length === 0) {
        message.info("No active loan products are available for your company.");
      }
    } catch (error) {
      console.error("Error fetching loan types:", error);
      message.error("Failed to load loan products");
    } finally {
      setLoading(false);
    }
  };

  const handleLoanTypeChange = (value, option) => {
    const selected = loanTypes.find((lt) => lt.id === value);
    setSelectedLoanType(selected);
    setMaxAmount(selected?.max_amount || 0);
    form.setFieldsValue({ amount: undefined });
  };

  const handleSubmit = async (values) => {
    setMsg({ type: "", text: "" });
    try {
      setSubmitting(true);

      const paymentDetails = calculateLoanPayments(
        selectedLoanType,
        values.amount
      );

      // console.log(paymentDetails);

      const payload = {
        ...values,
        company: companyId,
        loan_status: 2,
        status: "published",
        borrower: user.user_id,
        next_payment_date: paymentDetails.next_payment_date,
        next_payment_amount: paymentDetails.next_payment_amount,
        total_amount_due: paymentDetails.total_amount_due,
        interest_amount: paymentDetails.interest_amount,
        ...(selectedLoanType?.auto_split_payments &&
          paymentDetails.payment_schedule && {
            payment_schedule: paymentDetails.payment_schedule,
          }),
      };

      console.log("Loan application payload:", payload);

      await axios.post(
        "https://zedlenders.pickmesms.com/items/loans",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMsg({
        type: "success",
        text: "Application successful! Please check your email for confirmation.",
      });
      // onCancel();
      form.resetFields(); // Reset form after success
      setSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
      message.error(
        error.response?.data?.errors?.[0]?.message ||
          "Failed to submit loan application"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    {
      label: "Loan Type",
      name: "loan_product",
      type: "select",
      placeholder: "Select Loan Type",
      options: loanTypes,
      rules: [{ required: true, message: "Please select loan type!" }],
      props: {
        showSearch: true,
        optionFilterProp: "children",
        onChange: handleLoanTypeChange,
        disabled: loading,
      },
    },
    {
      label: "Loan Amount (ZMW)",
      name: "amount",
      type: "number",
      autoComplete: "off",
      rules: [
        { required: true, message: "Please enter loan amount!" },
        {
          validator: (_, value) => {
            if (value > maxAmount) {
              return Promise.reject(`Amount cannot exceed ${maxAmount}`);
            }
            return Promise.resolve();
          },
        },
      ],
      props: {
        prefix: "K",
        placeholder: `Maximum: ${maxAmount.toLocaleString()}`,
      },
    },
    {
      label: "Loan Purpose",
      name: "loan_purpose",
      type: "select",
      rules: [{ required: true, message: "Please select loan purpose!" }],
      options: [
        { label: "Business Investment", value: "business_investment" },
        { label: "Working Capital", value: "working_capital" },
        { label: "Equipment Purchase", value: "equipment_purchase" },
        { label: "Inventory Purchase", value: "inventory_purchase" },
        { label: "Home Improvement", value: "home_improvement" },
        { label: "Education", value: "education" },
        { label: "Medical Emergency", value: "medical_emergency" },
        { label: "Wedding", value: "wedding" },
        { label: "Vehicle Purchase", value: "vehicle_purchase" },
        { label: "Debt Consolidation", value: "debt_consolidation" },
        { label: "Agricultural Investment", value: "agricultural_investment" },
        { label: "Other", value: "other" },
      ],
      props: {
        showSearch: true,
        placeholder: "Select loan purpose",
      },
    },
  ];

  return (
    <Modal
      title="Apply for New Loan"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {fields.map((field) => (
            <Form.Item
              key={field.name}
              label={field.label}
              name={field.name}
              rules={field.rules}
            >
              {field.type === "select" ? (
                <Select
                  {...field.props}
                  options={field.options}
                  placeholder={field.placeholder}
                />
              ) : field.type === "textarea" ? (
                <Input.TextArea {...field.props} />
              ) : (
                <Input {...field.props} type={field.type} />
              )}
            </Form.Item>
          ))}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={submitting}
              disabled={submitted || !companyId}
            >
              Submit Application
            </Button>
          </Form.Item>
          {msg.text && (
            <Alert
              message={msg.text}
              type={msg.type}
              showIcon
              style={{
                marginBottom: 24,
                borderRadius: 8,
                background: msg.type === "success" ? "#f6ffed" : "#fff1f0",
                border: "none",
              }}
            />
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default ApplyForNewLoanModal;
